/**
 * Public driver trip sheet — /trip/:token
 * No login. Driver clocks in/out and updates livery statuses.
 * Clock in/out creates/closes driver_shifts which feed payroll.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Driver OAuth callback — exchanges session_id then returns to the trip sheet.
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export const TripAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const run = async () => {
      try {
        const params = new URLSearchParams(location.hash.replace('#', ''));
        const sessionId = params.get('session_id');
        if (!sessionId) { setError('No session ID found'); return; }
        const r = await fetch(`${API}/api/auth/driver/session`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Authentication failed');
        navigate(location.pathname, { replace: true, state: { actor: d } });
      } catch (e) { setError(e.message); }
    };
    run();
  }, [location, navigate]);

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, sans-serif', padding: 20 }}>
      {error ? (
        <div style={{ textAlign: 'center', maxWidth: 360 }} data-testid="trip-auth-error">
          <p style={{ fontSize: 14, fontWeight: 800, color: '#CC0000', margin: '0 0 8px' }}>Access Denied</p>
          <p style={{ fontSize: 12, color: '#525252' }}>{error}</p>
          <button onClick={() => navigate(location.pathname, { replace: true })} style={{ marginTop: 16, padding: '10px 20px', background: '#0A0A0A', color: '#FFF', border: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', cursor: 'pointer' }}>Back to Trip</button>
        </div>
      ) : (
        <p style={{ color: '#A3A3A3', fontSize: 13 }}>Signing you in…</p>
      )}
    </div>
  );
};

const STEPS = [
  { action: 'clock_in',    label: 'Clock In',           color: '#15803D' },
  { action: 'en_route',    label: 'En Route',           color: '#B45309' },
  { action: 'on_location', label: 'On Location',        color: '#B45309' },
  { action: 'pob',         label: 'Passenger On Board', color: '#CC0000' },
  { action: 'done',        label: 'Trip Complete',      color: '#15803D' },
  { action: 'clock_out',   label: 'Clock Out',          color: '#0A0A0A' },
];

export default function TripSheet() {
  const { token } = useParams();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [authState, setAuthState] = useState(null); // null=checking, 'ok', 'denied'
  const [actor, setActor] = useState(null);

  const load = useCallback(async () => {
    try {
      const me = await fetch(`${API}/api/auth/driver/me`, { credentials: 'include' });
      if (!me.ok) { setAuthState('denied'); return; }
      setActor(await me.json());
      setAuthState('ok');
      const r = await fetch(`${API}/api/trip/${token}`, { credentials: 'include' });
      if (!r.ok) throw new Error('Trip not found');
      setTrip(await r.json());
    } catch (e) { setError(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const doAction = async (action) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/trip/${token}/action`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Action failed');
      if (action === 'clock_out' && d.hours != null) toast.success(`Clocked out — ${d.hours} hrs logged for payroll`);
      else toast.success(STEPS.find(s => s.action === action)?.label || action);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const doneActions = new Set((trip?.trip_timeline || []).map(t => t.action));
  const nextIdx = STEPS.findIndex(s => !doneActions.has(s.action));

  if (authState === 'denied') return (
    <div style={{ minHeight: '100vh', background: '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, sans-serif', padding: 20 }}>
      <div style={{ background: '#FFFFFF', border: '1px solid #E5E5E5', padding: 28, maxWidth: 380, width: '100%', textAlign: 'center' }} data-testid="trip-signin">
        <p style={{ fontSize: 9, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: 0 }}>ACE · Driver Trip Sheet</p>
        <h1 style={{ fontSize: 20, fontWeight: 900, margin: '10px 0 6px' }}>Driver Sign-In</h1>
        <p style={{ fontSize: 12, color: '#525252', margin: '0 0 20px' }}>Sign in with the Google account on your driver profile to open this trip sheet and clock in.</p>
        <button onClick={signIn} style={{ width: '100%', padding: '14px', background: '#0A0A0A', color: '#FFFFFF', border: 0, fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' }} data-testid="trip-google-signin">
          Sign in with Google
        </button>
        <p style={{ fontSize: 10, color: '#A3A3A3', marginTop: 14 }}>Not registered? Ask dispatch to add your email to your driver profile.</p>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, sans-serif' }}>
      <p style={{ color: '#666' }} data-testid="trip-error">{error}</p>
    </div>
  );
  if (!trip) return (
    <div style={{ minHeight: '100vh', background: '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '-apple-system, sans-serif' }}>
      <p style={{ color: '#A3A3A3', fontSize: 13 }}>Loading trip…</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAFA', color: '#0A0A0A', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', padding: '0 0 60px' }} data-testid="trip-sheet">
      <Toaster position="top-center" theme="light" />
      <header style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E5E5', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ fontSize: 9, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: 0 }}>ACE · Driver Trip Sheet</p>
          <h1 style={{ fontSize: 22, fontWeight: 900, margin: '4px 0 0' }} data-testid="trip-client">{trip.client_name || 'Trip'}</h1>
          {trip.driver && <p style={{ fontSize: 11, color: '#666', margin: '2px 0 0' }}>Driver: {trip.driver.name}</p>}
          {actor && <p style={{ fontSize: 10, color: '#A3A3A3', margin: '2px 0 0' }} data-testid="trip-actor">Signed in as {actor.name || actor.email} ({actor.role})</p>}
        </div>
        <a href="/driver" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#CC0000', textDecoration: 'none', border: '1px solid #E5E5E5', padding: '6px 10px', flexShrink: 0 }} data-testid="trip-mytrips-link">My Trips</a>
      </header>

      <main style={{ maxWidth: 480, margin: '0 auto', padding: '16px' }}>
        {/* Trip details */}
        <section style={{ background: '#FFFFFF', border: '1px solid #E5E5E5', padding: 16, marginBottom: 16 }} data-testid="trip-details">
          {[
            ['Date', trip.event_date],
            ['Pickup Time', trip.pickup_time],
            ['Pickup', trip.pickup_address],
            ['Drop-off', trip.dropoff_address],
            ['Vehicle', trip.vehicle],
            ['Flight', trip.flight_number],
            ['Hours', trip.hours],
            ['Notes', trip.notes],
          ].filter(([, v]) => v).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid #F3F3F3' }}>
              <span style={{ fontSize: 10, color: '#A3A3A3', textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700, flexShrink: 0, paddingTop: 2 }}>{k}</span>
              <span style={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>{v}</span>
            </div>
          ))}
          {trip.client_phone && (
            <a href={`tel:${trip.client_phone}`} style={{ display: 'block', marginTop: 12, padding: '10px 0', textAlign: 'center', border: '1px solid #0A0A0A', color: '#0A0A0A', textDecoration: 'none', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }} data-testid="trip-call-client">
              📞 Call Client
            </a>
          )}
        </section>

        {/* Status buttons */}
        <p style={{ fontSize: 9, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '0 4px 10px' }}>Trip Status</p>
        <section style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
          {STEPS.map((s, i) => {
            const done = doneActions.has(s.action);
            const isNext = i === nextIdx;
            return (
              <button
                key={s.action}
                onClick={() => doAction(s.action)}
                disabled={busy || done || !isNext}
                style={{
                  padding: '16px', fontSize: 14, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em',
                  border: `2px solid ${done ? '#E5E5E5' : isNext ? s.color : '#E5E5E5'}`,
                  background: done ? '#F3F3F3' : isNext ? s.color : '#FFFFFF',
                  color: done ? '#A3A3A3' : isNext ? '#FFFFFF' : '#C4C4C4',
                  cursor: isNext ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
                data-testid={`trip-action-${s.action}`}
              >
                <span>{done ? '✓ ' : ''}{s.label}</span>
                {done && <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'none' }}>
                  {new Date((trip.trip_timeline || []).find(t => t.action === s.action)?.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>}
              </button>
            );
          })}
        </section>

        {/* Timeline */}
        {(trip.trip_timeline || []).length > 0 && (
          <section data-testid="trip-timeline">
            <p style={{ fontSize: 9, color: '#CC0000', textTransform: 'uppercase', letterSpacing: '0.3em', fontWeight: 700, margin: '0 4px 10px' }}>Timeline</p>
            <div style={{ background: '#FFFFFF', border: '1px solid #E5E5E5', padding: 12 }}>
              {trip.trip_timeline.map((t, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 12, borderBottom: i < trip.trip_timeline.length - 1 ? '1px solid #F3F3F3' : 'none' }}>
                  <span style={{ fontWeight: 700 }}>{t.label}</span>
                  <span style={{ color: '#666', fontFamily: 'monospace' }}>{new Date(t.at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
